"use client";

import { m } from "motion/react";
import { StaggerGroup, StaggerItem } from "@/lib/animation";
import { italicPopVariants } from "./hero-sticky.animation";
import type { RichtextNode, HeroStickyProps } from "./hero-sticky.interface";
import messages from "@/messages/en.json";

function renderNode(node: RichtextNode, key: number): React.ReactNode {
  if (node.type === "doc") {
    return node.content?.map((child, i) => renderNode(child, i));
  }

  if (node.type === "paragraph") {
    return (
      <span key={key} className="mt-4 block first:mt-0">
        {node.content?.map((child, i) => renderNode(child, i))}
      </span>
    );
  }

  if (node.type === "hard_break") {
    return <br key={key} />;
  }

  if (node.type === "text") {
    const italic = node.marks?.some((m) => m.type === "italic");
    const underline = node.marks?.some((m) => m.type === "underline");
    const link = node.marks?.find((m) => m.type === "link");

    let content: React.ReactNode;

    if (italic) {
      content = (
        <m.span
          className="text-copper-kettle inline font-accent leading-none"
          variants={italicPopVariants}
        >
          {node.text}
        </m.span>
      );
    } else if (underline) {
      content = (
        <span className="bg-[url('/underline-pink.svg')] bg-size-[100%_auto] bg-center bg-no-repeat pb-5">
          {node.text}
        </span>
      );
    } else {
      content = node.text;
    }

    if (link) {
      const attrs = link.attrs ?? {};
      const href = (attrs.href as string) ?? "#";
      const targetBlank = attrs.target === "_blank";

      return (
        <a
          key={key}
          href={href}
          className="underline"
          {...(targetBlank && { target: "_blank", rel: "noopener noreferrer" })}
        >
          {content}
          {targetBlank && (
            <span className="sr-only"> {messages.common.opensInNewTab}</span>
          )}
        </a>
      );
    }

    return <span key={key}>{content}</span>;
  }

  return null;
}

export function HeroSticky({
  label,
  heroTitle,
  heroDescription,
  heroTextContent,
  className,
}: HeroStickyProps) {
  return (
    <section className={className}>
      <StaggerGroup className="flex flex-col justify-center lg:mt-42.5">
        {label && (
          <StaggerItem
            as="p"
            className="font-accent text-copper-kettle mb-3 text-center lg:text-left text-page lg:text-page-lg  "
          >
            {label}
          </StaggerItem>
        )}
        {heroTitle && (
          <StaggerItem
            as="h1"
            className="font-display text-hop mb-6 text-center text-[48px] leading-14.25 font-bold lg:mb-7 lg:text-hero-lg lg:leading-18.5 lg:font-black lg:text-left"
          >
            {renderNode(heroTitle, 0)}
          </StaggerItem>
        )}
        {heroDescription && (
          <StaggerItem
            as="p"
            className="text-hop text-lg text-center leading-6.5 lg:text-left text-pretty"
          >
            {heroDescription}
          </StaggerItem>
        )}
        {heroTextContent && (
          <StaggerItem
            as="p"
            className="text-hop text-lg text-center leading-6.5 lg:text-left text-pretty"
          >
            {renderNode(heroTextContent, 0)}
          </StaggerItem>
        )}
      </StaggerGroup>
    </section>
  );
}
